// AnalyticsDashboard — لوحة تحليلات موجزة (3.4.11): إشغال + عيادة + إيراد.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

function Stat({ label, value, suffix, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: C.line, bgcolor: C.card, height: '100%' }}>
      <Typography variant="caption" sx={{ color: C.muted, fontWeight: 800 }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, color: color || C.text }}>
        {value}{suffix ? <Typography component="span" variant="body2" sx={{ color: C.muted, ml: 0.5 }}>{suffix}</Typography> : null}
      </Typography>
    </Paper>
  );
}

export default function AnalyticsDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/analytics/overview', { params: { from: range.from || undefined, to: range.to || undefined } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <SmartPage>
      <SmartHeader title={t('analytics', 'التحليلات')} subtitle={t('analytics_hint', 'نظرة موجزة: الإشغال وأداء العيادة والإيراد للفترة.')} />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField type="date" size="small" label={t('from', 'من')} InputLabelProps={{ shrink: true }} value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
        <TextField type="date" size="small" label={t('to', 'إلى')} InputLabelProps={{ shrink: true }} value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : data ? (
        <>
          <Typography variant="caption" sx={{ color: C.muted }}>{data.period.from} → {data.period.to}</Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6} md={3}><Stat label={t('active_horses', 'خيول نشطة')} value={data.counts.horses} /></Grid>
            <Grid item xs={6} md={3}><Stat label={t('owners', 'المُلّاك')} value={data.counts.owners} /></Grid>
            <Grid item xs={6} md={3}><Stat label={t('occupancy', 'الإشغال')} value={data.counts.occupancyPct ?? '—'} suffix={data.counts.occupancyPct != null ? '%' : ''} color={C.appbar} /></Grid>
            <Grid item xs={6} md={3}><Stat label={t('occupied_rooms', 'غرف مشغولة')} value={`${data.counts.occupied}/${data.counts.rooms}`} /></Grid>

            <Grid item xs={6} md={3}><Stat label={t('vet_records', 'سجلات عيادة')} value={data.clinic.vetRecords} /></Grid>
            <Grid item xs={6} md={3}><Stat label={t('farrier_records', 'سجلات بيطار')} value={data.clinic.farrierRecords} /></Grid>
            <Grid item xs={6} md={3}><Stat label={t('vet_revenue', 'إيراد العيادة')} value={money(data.revenue.vet)} /></Grid>
            <Grid item xs={6} md={3}><Stat label={t('farrier_revenue', 'إيراد البيطار')} value={money(data.revenue.farrier)} /></Grid>

            <Grid item xs={12} md={4}><Stat label={t('total_revenue', 'إجمالي الإيراد')} value={money(data.revenue.total)} color={C.green} /></Grid>
          </Grid>
        </>
      ) : null}
    </SmartPage>
  );
}
